import { useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Loader2, Upload, MicOff } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UseInlineVoiceInputReturn } from "@/hooks/use-inline-voice-input";

interface VoiceInputButtonProps {
  voiceInput: UseInlineVoiceInputReturn;
  onTranscript: (text: string) => void;
  className?: string;
  disabled?: boolean;
  showLabel?: boolean;
  label?: string;
}

export function VoiceInputButton({
  voiceInput,
  onTranscript: _onTranscript,
  className,
  disabled = false,
  showLabel = false,
  label,
}: VoiceInputButtonProps) {
  const { isRecording, status, error, micPermission, startRecording, stopRecording, reset } = voiceInput;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showFallbackTooltip, setShowFallbackTooltip] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const isLoading = status === "transcribing";
  const isUnavailable = micPermission === "unavailable" || !voiceInput.isMediaRecorderSupported;
  const isDenied = micPermission === "denied";

  const handleClick = useCallback(async () => {
    if (disabled || isLoading) return;

    if (isUnavailable) {
      setShowFallbackTooltip(true);
      setTimeout(() => setShowFallbackTooltip(false), 3000);
      fileInputRef.current?.click();
      return;
    }

    if (isDenied) {
      return;
    }

    if (isRecording) {
      stopRecording();
    } else {
      reset();
      await startRecording();
    }
  }, [disabled, isLoading, isUnavailable, isDenied, isRecording, stopRecording, reset, startRecording]);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadError(null);

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = async () => {
        if (typeof reader.result !== "string") return;
        const base64 = reader.result.split(",")[1];
        if (!base64) return;
        try {
          const result = await voiceInput["transcribeAudio" as never] as never;
          void result;
        } catch {
          setUploadError("Upload transcription failed. Please try again.");
        }
      };
      reader.onerror = () => setUploadError("Could not read file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [voiceInput]
  );

  const getAriaLabel = () => {
    if (isUnavailable) return "Upload voice note (live recording not supported)";
    if (isDenied) return "Microphone access denied";
    if (isLoading) return "Transcribing...";
    if (isRecording) return "Stop recording";
    return "Start voice input";
  };

  const errorMsg = error ?? uploadError;

  const pillLabel = isLoading
    ? "Transcribing…"
    : isRecording
    ? "Stop recording"
    : isUnavailable
    ? "Upload audio"
    : isDenied
    ? "Mic blocked"
    : label ?? "Speak your idea";

  return (
    <div className={cn("relative inline-flex flex-col items-center", className)}>
      <div className="relative">
        <motion.button
          type="button"
          aria-label={getAriaLabel()}
          onClick={handleClick}
          disabled={disabled || isLoading || isDenied}
          whileHover={!disabled && !isLoading && !isDenied ? { scale: showLabel ? 1.03 : 1.08 } : {}}
          whileTap={!disabled && !isLoading && !isDenied ? { scale: 0.95 } : {}}
          data-testid="voice-input-button"
          className={cn(
            showLabel
              ? cn(
                  "relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 border",
                  isRecording
                    ? "bg-destructive/10 text-destructive border-destructive/30 shadow-sm"
                    : isLoading
                    ? "bg-primary/5 text-primary/60 border-primary/20 cursor-wait"
                    : isDenied
                    ? "bg-secondary text-muted-foreground border-border opacity-50 cursor-not-allowed"
                    : "bg-primary/10 text-primary border-primary/25 hover:bg-primary/20 hover:border-primary/40 cursor-pointer shadow-sm"
                )
              : cn(
                  "relative w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                  isUnavailable
                    ? "bg-secondary text-muted-foreground hover:bg-secondary/80 cursor-pointer"
                    : isDenied
                    ? "bg-secondary text-muted-foreground cursor-not-allowed opacity-50"
                    : isLoading
                    ? "bg-secondary text-muted-foreground cursor-wait"
                    : isRecording
                    ? "bg-destructive text-destructive-foreground shadow-lg shadow-destructive/30"
                    : "bg-secondary text-muted-foreground hover:bg-primary/10 hover:text-primary cursor-pointer"
                )
          )}
        >
          {isUnavailable ? (
            <Upload className="w-3.5 h-3.5" />
          ) : isDenied ? (
            <MicOff className="w-3.5 h-3.5" />
          ) : isLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Mic className="w-3.5 h-3.5" />
          )}
          {showLabel && <span>{pillLabel}</span>}

          {isRecording && !showLabel && (
            <>
              <span
                className="absolute inset-0 rounded-full border-2 border-destructive animate-ping opacity-70"
                style={{ animationDuration: "1.4s" }}
              />
              <span
                className="absolute inset-0 rounded-full border border-destructive animate-ping opacity-40"
                style={{ animationDuration: "2s" }}
              />
            </>
          )}
        </motion.button>

        {isRecording && !showLabel && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-destructive border-2 border-background"
          />
        )}
      </div>

      <AnimatePresence>
        {showFallbackTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 whitespace-nowrap text-xs bg-popover text-popover-foreground border border-border rounded-lg px-2.5 py-1.5 shadow-lg pointer-events-none"
          >
            Your browser doesn't support live recording — uploading a voice note
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {errorMsg && status === "error" && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 w-48 text-xs bg-destructive/10 text-destructive border border-destructive/30 rounded-lg px-2.5 py-1.5 shadow-lg text-center"
          >
            {errorMsg}
          </motion.div>
        )}
      </AnimatePresence>

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={handleFileUpload}
        aria-label="Upload voice note"
      />
    </div>
  );
}

interface InlineRecordingIndicatorProps {
  recordingTime: number;
  formatTime: (s: number) => string;
  className?: string;
}

export function InlineRecordingIndicator({
  recordingTime,
  formatTime,
  className,
}: InlineRecordingIndicatorProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={cn(
        "flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-destructive/10 border border-destructive/30 text-destructive text-xs font-medium",
        className
      )}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />
      {formatTime(recordingTime)}
    </motion.div>
  );
}
